import { ChangeDetectionStrategy, Component, Inject, Input, OnInit, Optional } from '@angular/core';
import {
	ANALYTICS_PROVIDER,
	AnalyticsClass,
	ImpressionArgsFactory,
	DatalayerService,
	ImpressionTrackingBehaviourType,
	MaybeExternalLinkDirective,
	promoClickAnalytics,
	PromoImpressionArgs,
	TealiumPromoList,
	TrackImpressionDirective,
} from '@woolworthsnz/styleguide';
import { ImageProperties } from '../../ui-models';
import { TargetTranslatePipe } from '../../pipes/target-translate.pipe';
import { NgIf, NgTemplateOutlet } from '@angular/common';

@Component({
	selector: 'dc-image, [dcImage]',
	template: `
		<ng-container *ngIf="image">
			<ng-container *ngIf="image.link; else imageTemplate">
				<a
					[href]="link"
					[target]="target | toTarget"
					maybeExternal
					(click)="handleOnClick()"
					[cdxTrackImpression]="trackingData"
					[trackingBehaviour]="impressionTrackingBehaviour"
				>
					<ng-container *ngTemplateOutlet="linkedImage"></ng-container>
				</a>
			</ng-container>
			<ng-template #imageTemplate>
				<img
					[id]="ariaLabelId"
					[src]="src"
					[alt]="image.altText || ''"
					[attr.title]="image.text || null"
					[cdxTrackImpression]="trackingData"
					[trackingBehaviour]="impressionTrackingBehaviour"
				/>
			</ng-template>
			<ng-template #linkedImage>
				<span
					class="action-button-layout cta cta--{{ image.serializedProperties?.ctaposition }}"
					*ngIf="image.serializedProperties?.cta"
				>
					{{ image.serializedProperties?.linkText }}
				</span>
				<ng-container *ngTemplateOutlet="imageTemplate"></ng-container>
			</ng-template>
		</ng-container>
	`,
	styleUrls: ['./image.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, MaybeExternalLinkDirective, NgTemplateOutlet, TrackImpressionDirective, TargetTranslatePipe],
})
export class ImageComponent implements OnInit {
	@Input() isCarouselItem = false;
	@Input() carouselName?: string | null;
	@Input() ariaLabelId = '';
	@Input() position = 0;

	@Input() set image(i: ImageProperties) {
		this._image = i;
	}

	get image(): ImageProperties {
		return this._image;
	}

	private _image: ImageProperties;
	private _trackingData: PromoImpressionArgs;

	get src(): string {
		return this.image && this.image.imagePath;
	}

	get link(): string {
		return this.image?.link;
	}

	get target(): string {
		return this.image?.linkTarget;
	}

	get trackingData(): PromoImpressionArgs {
		return this._trackingData;
	}

	impressionTrackingBehaviour = ImpressionTrackingBehaviourType.TrackOnFirstIntersectionOnly;

	constructor(
		private datalayerService: DatalayerService,
		@Optional() @Inject(ANALYTICS_PROVIDER) private tealiumService: AnalyticsClass
	) {}

	ngOnInit(): void {
		// The ID is required for promo tracking data
		this.image.id = this.ariaLabelId;

		if (!this.isCarouselItem) {
			this._trackingData = {
				id: this.image.id || this.image.imagePath,
				name: this.image.name || this.image.text,
				creative: 'Tile - Single',
				position: `${this.position + 1}`,
				imagePath: this.image.imagePath,
				link: this.image.link,
			};
		} else {
			// Disable promo impression tracking for the image, since it will be handled by the carousel
			this.impressionTrackingBehaviour = ImpressionTrackingBehaviourType.Disabled;

			// Tracking data to be used by the click event
			this._trackingData = ImpressionArgsFactory.createCarouselPromoImpressionArgs(
				this.image,
				this.carouselName ?? null,
				this.position + 1
			);
		}
	}

	handleOnClick(): void {
		this.datalayerService.trackPromoClick({
			...this.trackingData,
		});

		const promoList: TealiumPromoList = {
			media_label: [`${this.image.imagePath}`],
			media_category: [`${this.image.text}`],
			media_carousel_slot: [`${this.isCarouselItem ? this.position + 1 : ''}`],
			media_destination_link: [`${this.link}`],
		};

		this.tealiumService.link({
			...promoClickAnalytics,
			...promoList,
			media_type: `${this.isCarouselItem ? 'Carousel' : 'Tile - Single'}`,
		});
	}
}
